import classNames from 'classnames';
import { ComponentChild, h, JSX } from 'preact';

import { MinusIcon, PlusIcon } from './Icons';

export interface NumberInputProps {
    value: number;
    onInput?: () => void;
    onChange?: (value: number) => void;
    min?: number;
    max?: number;
    isDisabled?: boolean;
    inputClass?: string;
}

const clampValue = (props: NumberInputProps, value: number): number => {
    if (typeof props.min === 'number' && value < props.min) {
        return props.min;
    } else if (typeof props.max === 'number' && value > props.max) {
        return props.max;
    } else {
        return value;
    }
};

const getNewValue = (props: NumberInputProps, input: HTMLInputElement): number => {
    const numValue = Number(input.value);

    return clampValue(props, numValue);
};

const isDisabled = (props: NumberInputProps, diff: number): boolean => {
    if (props.isDisabled) {
        return true;
    }

    const newValue = props.value + diff;

    if (typeof props.min === 'number' && newValue < props.min) {
        return true;
    }

    if (typeof props.max === 'number' && newValue > props.max) {
        return true;
    }

    return false;
};

const renderButton = (props: NumberInputProps, diff: number, icon: ComponentChild): JSX.Element => (
    <button
        class={classNames('inline-flex items-center justify-center gap-1 rounded-lg border transition-colors outline-none min-h-[2.25rem] text-base px-4 py-2.5 font-semibold h-12', {
            'cursor-pointer bg-white border-gray-300 text-gray-800 focus:ring-offset-2 focus:ring-2 focus:ring-inset focus:ring-primary-600 focus:text-primary-600 focus:bg-primary-50 focus:border-primary-600 hover:bg-gray-50': !isDisabled(props, diff),
            'cursor-default delay-300 bg-gray-200/50 border-transparent text-gray-400': isDisabled(props, diff),
        })}
        onClick={(event): void => {
            event.stopPropagation();
            const newValue = clampValue(props, props.value + diff);
            props.onChange?.(newValue);
        }}
        disabled={isDisabled(props, diff)}
    >
        <div
            class={classNames('', {
                'opacity-30 delay-300': isDisabled(props, diff),
            })}
        >
            {icon}
        </div>
    </button>
);

export const NumberInput = (props: NumberInputProps): JSX.Element => (
    <div class='flex space-x-1 md:space-x-2'>
        <div
            class={classNames('', {
                'opacity-30 delay-300': props.isDisabled,
            })}
        >
            {renderButton(props, -1, <MinusIcon />)}
        </div>
        <input
            class={classNames('w-12 sm:w-20 h-12 min-h-[2.25rem] px-4 py-2.5 bg-white text-gray-800 text-base font-semibold inline-flex items-center justify-center text-center gap-1 rounded-lg border transition-colors outline-none focus:ring-offset-2 focus:ring-2 focus:ring-inset border-gray-300 hover:bg-gray-50 focus:ring-primary-600 focus:text-primary-600 focus:bg-primary-50 focus:border-primary-600', props.inputClass)}
            value={props.value}
            onInput={props.onInput}
            onChange={(event): void => props.onChange?.(getNewValue(props, event.currentTarget))}
            onClick={(event): void => event.stopPropagation()}
            disabled={props.isDisabled}
        />
        <div
            class={classNames('', {
                'opacity-30 delay-300': props.isDisabled,
            })}
        >
            {renderButton(props, 1, <PlusIcon />)}
        </div>
    </div>
);
